import { For, Show, createSignal } from 'solid-js'
import { getMetricDefinition } from '@fasting-timer/shared'

import { useI18n } from '../../i18n'
import {
   type MetricId,
   enableMetric,
   enabledMetricIds,
   getAvailableMetricIds,
   selectedMetricId,
   setSelectedMetricId,
} from '../../state/measurementsUiStore'

/**
 * Returns the translation key for a metric's name.
 *
 * @param metricId - The metric id.
 * @returns The label key.
 */
function getMetricLabelKey(metricId: MetricId) {
   return metricId === 'weight' ? 'Weight' : (
         (getMetricDefinition(metricId)?.labelKey ?? metricId)
      )
}

/**
 * Renders the segmented control that switches between metrics in the
 * measurements tab, plus a "+" menu for enabling registry metrics.
 *
 * @returns The picker element.
 */
const MetricPicker = () => {
   const t = useI18n()
   const [isMenuOpen, setMenuOpen] = createSignal(false)

   return (
      <div class="space-y-2">
         <div
            id="metric-picker"
            role="tablist"
            aria-label={t('Measurements')}
            class="flex gap-1 rounded-xl border border-edge-soft bg-surface-raised p-1"
         >
            <For each={enabledMetricIds()}>
               {metricId => (
                  <button
                     type="button"
                     role="tab"
                     aria-selected={selectedMetricId() === metricId ? 'true' : 'false'}
                     onClick={() => setSelectedMetricId(metricId)}
                     class="flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition"
                     classList={{
                        'bg-surface-control text-ink': selectedMetricId() === metricId,
                        'text-ink-secondary hover:bg-surface-control/60':
                           selectedMetricId() !== metricId,
                     }}
                  >
                     {t(getMetricLabelKey(metricId))}
                  </button>
               )}
            </For>
            <Show when={getAvailableMetricIds().length > 0}>
               <button
                  type="button"
                  id="metric-picker-add"
                  onClick={() => setMenuOpen(!isMenuOpen())}
                  aria-expanded={isMenuOpen() ? 'true' : 'false'}
                  aria-controls="metric-picker-menu"
                  aria-label={t('Add metric')}
                  class="rounded-lg px-3 py-2 text-sm font-semibold text-ink-secondary transition hover:bg-surface-control/60"
               >
                  +
               </button>
            </Show>
         </div>
         <Show when={isMenuOpen() && getAvailableMetricIds().length > 0}>
            <div
               id="metric-picker-menu"
               class="flex flex-wrap gap-2 rounded-xl border border-edge-soft bg-surface-raised p-2"
            >
               <span class="w-full text-xs uppercase tracking-wider text-ink-muted">
                  {t('Add metric')}
               </span>
               <For each={getAvailableMetricIds()}>
                  {metricId => (
                     <button
                        type="button"
                        onClick={() => {
                           enableMetric(metricId)
                           setMenuOpen(false)
                        }}
                        class="px-3 py-1.5 rounded-full text-sm border border-edge bg-surface-raised text-ink-secondary transition hover:bg-surface-control"
                     >
                        {t(getMetricLabelKey(metricId))}
                     </button>
                  )}
               </For>
            </div>
         </Show>
      </div>
   )
}

export default MetricPicker
